import { isDateAfter, isDateBefore, isSameDay, parseDateString } from '@co-3/ctrl-ui/common';
import { format } from 'date-fns';
import { Observable, map } from 'rxjs';

const today = new Date();

/**
 * @description the type of payment method. There are two types
 * atm: 1) "BankData", ie. SEPA, or 2) "CashTransfer", which means
 * the customer has to send the money themselves, for instance with
 * a standing order. Until decided otherwise only "BankData" will be
 * in use.
 */
export enum PaymentMethodType {
    BankData = 'BankData',
    CashTransfer = 'CashTransfer',
}

export type PaymentMethodTypeDto = 'BankData' | 'CashTransfer';

const paymentMethodTypeFromDto = (dto: 'BankData' | 'CashTransfer'): PaymentMethodType => {
    switch (dto) {
        case 'BankData':
            return PaymentMethodType.BankData;
        case 'CashTransfer':
            return PaymentMethodType.CashTransfer;
        default:
            throw new Error('paymentMethodTypeFromDto: unable to convert to PaymentMethodType - $type is unknown');
    }
};

const paymentMethodTypeToDto = (paymentMethodType: PaymentMethodType): PaymentMethodTypeDto => {
    switch (paymentMethodType) {
        case PaymentMethodType.BankData:
            return 'BankData';
        case PaymentMethodType.CashTransfer:
            return 'CashTransfer';
    }
};

/**
 * @description represents the way a user pays for a contract
 * at a certain point in time. `PaymentMethod`s are handled on
 * a per contract basis. For each contract there should be one
 * currently active payment method, and there might be several
 * outdated and queued methods. outdated methods are not in effect
 * anymore and should not be changed by us, while queued methods
 * are methods that will come into effect some time in the future
 * and then replace the currently active method (which will become
 * outdated). Outdated methods should be considered readonly and
 * never be updated or removed by the frontend.
 */
export interface PaymentMethod {
    readonly id: number;
    /**
     * @description id of the contract this payment method belongs to
     */
    contractId: number;
    /**
     * @description the type of payment method. There are two types
     * atm: 1) "BankData", ie. SEPA, or 2) "CashTransfer", which means
     * the customer has to send the money themselves, for instance with
     * a standing order. Until decided otherwise only "BankData" will be
     * in use.
     */
    type: PaymentMethodType;
    /**
     * @description name of the account holder. Necessary when
     * `type` is "BankData"
     */
    accountHolder: string;
    /**
     * @description the country the bank is located in. Only valid when
     * `type` is "BankData"
     */
    bankCountry: string;
    /**
     * @description name of the bank. Necessary when `type` is "BankData"
     */
    bankName: string;
    /**
     * @description IBAN of SEPA. Necessary when `type` is "BankData"
     */
    iban: string;
    /**
     * @description BIC for SEPA. Only necessary when `bankCountry` is not
     * germany and only valid when `type` is "BankData"
     */
    bic: string;
    /**
     * @description the date this payment method comes into effect.
     */
    validFrom: Date;
    /**
     * @description the date this payment method stops being used. Should
     * only ever be set by the backend.
     */
    validTo: Date | null;

    // the following properties should never be updated by us.
    // we still keep them because the API expects them to be
    // there, though:
    readonly mandate?: string;
    readonly mandateDate?: string;
    readonly ticketNumber?: string | null;
    readonly addedAt?: string | null;
    readonly addedBy?: string | null;
    readonly updatedAt?: string | null;
    readonly updatedBy?: string | null;
}

/**
 * @description data transfer object for payment methods. Please
 * note that the entity as described in the API documentation
 * has more properties than listed here, but most of them seem
 * to be irrelevant for now.
 */
export interface PaymentMethodDto {
    readonly id: number;
    contractId: string;
    $type: 'BankData' | 'CashTransfer';
    accountHolder: string;
    bankCountry: string;
    bankName: string;
    iban: string;
    bic: string;
    /**
     * @description date in format `yyyy-MM-dd`
     */
    validFrom: string;
    /**
     * @description date in format `yyyy-MM-dd`
     */
    validTo: string | null;

    // the following properties should never be updated by us:
    readonly mandate?: string | null;
    readonly mandateDate?: string | null;
    readonly ticketNumber?: string | null;
    readonly addedAt?: string | null;
    readonly addedBy?: string | null;
    readonly updatedAt?: string | null;
    readonly updatedBy?: string | null;
}

export const paymentMethodFromDto = (dto: PaymentMethodDto): PaymentMethod => {
    const { $type, validFrom, validTo, contractId, ...rest } = dto;

    return {
        ...rest,
        contractId: parseInt(contractId, 10),
        type: paymentMethodTypeFromDto($type),
        validFrom: parseDateString(validFrom, 'yyyy-MM-dd'),
        validTo: dto.validTo ? parseDateString(validTo, 'yyyy-MM-dd') : null,
    };
};

export function paymentMethodToDto(paymentMethod: PaymentMethod): PaymentMethodDto;
export function paymentMethodToDto(paymentMethod: Omit<PaymentMethod, 'id'>): Omit<PaymentMethodDto, 'id'>;
export function paymentMethodToDto(
    paymentMethod: PaymentMethod | Omit<PaymentMethod, 'id'>,
): PaymentMethodDto | Omit<PaymentMethodDto, 'id'> {
    const { type, validFrom, validTo, contractId, ...rest } = paymentMethod;

    return {
        // $type needs to come first or the API throws a validation error
        $type: paymentMethodTypeToDto(type),
        ...rest,
        contractId: '' + contractId,
        validFrom: format(validFrom, 'yyyy-MM-dd'),
        validTo: paymentMethod.validTo ? format(validTo, 'yyyy-MM-dd') : null,
    };
}

/**
 * @description there should be only one currently active payment method for
 * each contract. The currently active method has a `validFrom` date that is either
 * in the past or the current day, and a `validTo` date that is either in the
 * future or `null`.
 * @param {PaymentMethod[]} paymentMethods array of `PaymentMethod`s to search through
 * @returns {PaymentMethod | undefined} the currently active payment method or undefined if not found
 */
export const getCurrentlyActivePaymentMethod = (paymentMethods: PaymentMethod[]): PaymentMethod | undefined =>
    paymentMethods.find((paymentMethod: PaymentMethod) => isCurrentlyActive(paymentMethod));

/**
 * @description like {@link getCurrentlyActivePaymentMethod}, but works on and
 * returns an observable of `PaymentMethod`
 */
export const getCurrentlyActivePaymentMethod$ = (
    paymentMethods$: Observable<PaymentMethod[]>,
): Observable<PaymentMethod | undefined> =>
    paymentMethods$.pipe(map((paymenetMethods: PaymentMethod[]) => getCurrentlyActivePaymentMethod(paymenetMethods)));

/**
 * @description returns all the queued payment methods from an array of
 * payment methods. Queued methods are payment methods that will come into
 * effect at a later time. A payment method is considered queued when
 * `validFrom` lies in the future and `validTo` either also lies in the future
 * or is `null`
 * @param {PaymentMethod[]} paymentMethods array of `PaymentMethod`s to filter for queued payment methods
 * @returns {PaymentMethod[]} array of queued payment methods
 */
export const getQueuedPaymentMethods = (paymentMethods: PaymentMethod[]): PaymentMethod[] =>
    paymentMethods.filter((paymentMethod: PaymentMethod) => isQueued(paymentMethod));

/**
 * @description like {@link getQueuedPaymentMethods}, but works on and
 * returns an observable of `PaymentMethod`s.
 */
export const getQueuedPaymentMethods$ = (paymentMethods$: Observable<PaymentMethod[]>): Observable<PaymentMethod[]> =>
    paymentMethods$.pipe(map((paymentMethods: PaymentMethod[]) => getQueuedPaymentMethods(paymentMethods)));

/**
 * @description checks wether a `PaymentMethod` can be considered queued.
 * Queued methods are payment methods that will come into
 * effect at a later time. A payment method is considered queued when
 * `validFrom` lies in the future and `validTo` either also lies in the future
 * or is `null`. Queued methods may be edited and deleted.
 */
export const isQueued = (paymentMethod: PaymentMethod | Omit<PaymentMethod, 'id'>): boolean =>
    isDateAfter(paymentMethod.validFrom, today) &&
    (paymentMethod.validTo === null || isDateAfter(paymentMethod.validTo, today));

/**
 * @description checks wether a `PaymentMethod` is the currently active method.
 * A `PaymentMethod` is considered currently active when `validFrom` is either
 * in the past or the current day, and `validTo` is either `null`, a date in
 * the future or the current day. The currently active method may be edited,
 * but not deleted.
 */
export const isCurrentlyActive = (paymentMethod: PaymentMethod | Omit<PaymentMethod, 'id'>): boolean =>
    (isDateBefore(paymentMethod.validFrom, today) || isSameDay(paymentMethod.validFrom, today)) &&
    (paymentMethod.validTo === null ||
        isDateAfter(paymentMethod.validTo, today) ||
        isSameDay(paymentMethod.validTo, today));
